import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Button from "@mui/material/Button";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import "./FinalQuoteModel.css";
import { notify } from "../../../../../../../helpers/notify";
import { setdata } from "../../../../../../../helpers/helpers";
import { getCardSetupIntent } from "../../../../../../../services/CreditCard/stripe.services";
import StripeCreditCardForm from "../StripeCreditCardForm";
import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import AddressDetails from "./Components/addressDetails";
import PriceDetails from "./Components/priceDetails";
import PendingBalanceWrapper from "./Components/PendingBalance/PendingBalanceWrapper";

export default function FinalQuoteModel({
  StepCheck,
  SetStepCheck,
  Flist,
  Tlist,
  CreateShipment,
  Plist,
}) {
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

  // core states
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentType, setPaymentType] = useState("pay_via_balance");
  const [addOtherCard, setAddOtherCard] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalEventHolder, setGlobalEventHolder] = useState(null);
  const childRef = useRef();

  const PROCESSING_FEE = 0.03;
  const OUTSTANDING_LIMIT = 10;

  // SingleQuoteData destructure (note: credit_card_payment_method_id is top-level on StepCheck in original code)
  const {
    user_balance: rawBalance,
    total: rawTotal,
    payment_methods = [],
    response_from,
    account_type,
    comp_name,
  } = StepCheck?.SingleQuoteData || {};

  const userBalance = parseFloat(rawBalance) || 0;
  const total = parseFloat(rawTotal) || 0;
  const hasPaymentMethods = payment_methods?.length > 0;

  // computed amounts
  const processingFee = total * PROCESSING_FEE;
  const totalWithFee = total + processingFee;

  // --- get stripe setup intent ---
  const getCreditCardSetup = async () => {
    setLoading(true);
    try {
      const response = await getCardSetupIntent();
      setClientSecret(response.data.result.client_secret);
    } catch (error) {
      console.error("getCardSetupIntent error:", error);
      notify.error(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  // --- handle initial/default selections and whether to force add-new-card ---
  useEffect(() => {
    // if balance insufficient, default to card
    if (userBalance < total) {
      setPaymentType("pay_via_card");
    }

    // if no saved cards and payment required, show add-new-card and request client secret
    const needCardAndNone = userBalance < total && !hasPaymentMethods;
    if (needCardAndNone) {
      setAddOtherCard(true);
      if (!clientSecret) {
        getCreditCardSetup();
      }
    }

    // set default paymentMethodId when payment methods exist
    if (hasPaymentMethods) {
      const defaultCard = payment_methods.find(
        (card) => card?.card?.is_default === true
      );
      setPaymentMethodId(defaultCard?.id || null);
    }
    // we intentionally omit StepCheck itself from deps to avoid infinite loops;
    // this effect depends on SingleQuoteData-derived values, so include them.
  }, [userBalance, total, hasPaymentMethods, clientSecret, payment_methods]);

  // --- THIS restores the original behavior: when StepCheck["credit_card_payment_method_id"] is set (by clicking third Accept
  // or by Stripe flow), run CreateShipment with the saved event (globalEventHolder).
  useEffect(() => {
    const topLevelCardId = StepCheck?.credit_card_payment_method_id;
    if (addOtherCard === false && topLevelCardId) {
      // same as previous working code — fire CreateShipment using stored event
      CreateShipment(globalEventHolder);
    }
  }, [
    StepCheck?.credit_card_payment_method_id,
    globalEventHolder,
    addOtherCard,
  ]);

  // --- when user switches to pay_via_balance, clear any top-level credit_card_payment_method_id (same as previous)
  useEffect(() => {
    if (paymentType === "pay_via_balance") {
      SetStepCheck((prev) => ({
        ...prev,
        credit_card_payment_method_id: "",
      }));
    }
  }, [paymentType, SetStepCheck]);

  const isViaBoltOrCustom =
    response_from === "Via Bolt" ||
    account_type === "customCarrier" ||
    account_type === "client";
  const hasOutstandingBalance = userBalance < -OUTSTANDING_LIMIT;
  const insufficientFunds = userBalance < total;

  return (
    <Modal
      show={StepCheck["Final_Order"]}
      size="lg"
      centered
      className="modal__shipment_confirmation"
    >
      <Modal.Header>
        <Modal.Title>Shipment Confirmation</Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-0">
        {StepCheck["Final_Order"] === true && (
          <>
            <AddressDetails Flist={Flist} Tlist={Tlist} />
            <PriceDetails
              StepCheck={StepCheck}
              Plist={Plist}
              Charge={
                paymentType === "pay_via_card" && isViaBoltOrCustom
                  ? StepCheck?.withCardFinalCharge
                  : StepCheck?.withoutCardFinalCharge
              }
            />

            {(isViaBoltOrCustom || hasOutstandingBalance) && (
              <Row className="bg-brand text-center text-white">
                <small>
                  <b className="modal__shipment_confirmation_sec_title">
                    Payment
                  </b>
                </small>
              </Row>
            )}

            {hasOutstandingBalance && (
              <Row className="my-1">
                <Col md={6}>
                  <small>
                    <b>Outstanding Balance: </b>
                  </small>
                </Col>
                <Col md={6}>
                  <small style={{ color: "red" }}>
                    (Your outstanding balance of $
                    {setdata(() => Math.abs(userBalance), false)} exceeds the $
                    {OUTSTANDING_LIMIT} limit. Please clear it to continue
                    shipping.)
                  </small>
                </Col>
              </Row>
            )}

            {hasOutstandingBalance
              ? null
              : isViaBoltOrCustom && (
                  <>
                    {/* Payment Type Options */}
                    <Row className="my-1">
                      <Col md={12}>
                        <small>
                          <div
                            style={{
                              color: insufficientFunds
                                ? "var(--color-ui-text-subtle)"
                                : "var(--color-ui-text)",
                            }}
                          >
                            <input
                              type="radio"
                              value="pay_via_balance"
                              checked={paymentType === "pay_via_balance"}
                              onChange={() => {
                                setPaymentType("pay_via_balance");
                                setAddOtherCard(false);
                              }}
                              disabled={insufficientFunds}
                              name="pay-radio"
                              style={{ accentColor: "var(--color-brand)" }}
                            />{" "}
                            Pay via Account Balance (
                            <span
                              style={{
                                color: userBalance < 0 ? "red" : "",
                              }}
                            >
                              {rawBalance}
                            </span>
                            )
                            {insufficientFunds && (
                              <span style={{ color: "red" }}>
                                {" "}
                                Insufficient funds
                              </span>
                            )}
                            <span style={{ color: "green" }}>
                              {" "}
                              (Save approx 3% via Direct Top-up)
                            </span>
                          </div>
                          <input
                            type="radio"
                            name="pay-radio"
                            value="pay_via_card"
                            checked={paymentType === "pay_via_card"}
                            onChange={() => {
                              setPaymentType("pay_via_card");
                              if (payment_methods?.length == 0) {
                                setAddOtherCard(true);
                                if (!clientSecret) getCreditCardSetup();
                              }
                            }}
                            style={{ accentColor: "var(--color-brand)" }}
                          />{" "}
                          Pay via Credit Card
                        </small>
                      </Col>
                    </Row>

                    {/* Card Selection */}
                    {paymentType === "pay_via_card" && (
                      <Row className="my-1">
                        <Col md={12} style={{ marginLeft: "15px" }}>
                          <div className="payViaCard_main_wrap">
                            {hasPaymentMethods &&
                              payment_methods.map((card, index) => (
                                <div
                                  className="card__main_box_wrap"
                                  key={index}
                                >
                                  <div className="card__main_box">
                                    <input
                                      type="radio"
                                      name="radio_card"
                                      onChange={() => {
                                        setPaymentMethodId(card?.id);
                                        setAddOtherCard(false);
                                      }}
                                      checked={paymentMethodId === card?.id}
                                      className="card__main_box_radio"
                                    />
                                    <div className="card__main_box_header">
                                      <h2>Charge amount using this </h2>
                                      <strong
                                        style={{
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {card.card.brand}
                                      </strong>
                                    </div>
                                    <div className="card__main_box_cardNum">
                                      {"xxxx xxxx xxxx "}
                                      {card.card.last4}
                                    </div>

                                    {card.card?.is_default == true ? (
                                      <div className="card__main_box_default_tag">
                                        Default
                                      </div>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>
                              ))}

                            {hasPaymentMethods && (
                              <div className="newCard__main_wrapper">
                                <input
                                  type="radio"
                                  name="radio_card"
                                  checked={addOtherCard === true}
                                  onChange={() => {
                                    setAddOtherCard(true);
                                    if (!clientSecret) {
                                      getCreditCardSetup();
                                    }
                                  }}
                                  style={{ marginRight: "5px" }}
                                />

                                <span
                                  onClick={() => {
                                    setAddOtherCard(true);
                                    if (!clientSecret) {
                                      getCreditCardSetup();
                                    }
                                  }}
                                >
                                  Pay using a new card
                                </span>
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Stripe Form */}
                    <Row>
                      <Col md={12}>
                        {loading ? <CircularLoader /> : ""}
                        {paymentType === "pay_via_card" &&
                          addOtherCard &&
                          stripePromise &&
                          clientSecret && (
                            <Elements
                              stripe={stripePromise}
                              options={{ clientSecret }}
                            >
                              <StripeCreditCardForm
                                ref={childRef}
                                StepCheck={StepCheck}
                                SetStepCheck={SetStepCheck}
                                CreateShipment={CreateShipment}
                              />
                            </Elements>
                          )}
                      </Col>
                    </Row>
                  </>
                )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {hasOutstandingBalance ? (
          <PendingBalanceWrapper
            balance={setdata(() => Math.abs(userBalance), false)}
            SetStepCheck={SetStepCheck}
          />
        ) : (
          <>
            <div className="modal__shipment_confirmation_disclamer_wrap footer__disclamer_wrap">
              <strong>Disclaimer:</strong> Your use of the site is governed by
              the{" "}
              <a
                href="https://hajexbolt.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms
              </a>{" "}
              of Use. In accordance with the Terms, Carriers are solely
              responsible for providing accurate and reliable information and
              services to Shippers. Shippers agree to hold Bolt harmless from
              any claims of liability or damage, including consequential
              damages, arising from booking or shipment of freight. We recommend
              that you consult the Terms of Use and the Default Rates Table to
              understand your rights and obligations when using the Site.
              {comp_name === "Canpar" &&
                response_from !== "Via Bolt" &&
                account_type !== "default" && (
                  <>
                    <br />
                    <strong>Custom Account:</strong>
                    You're booking under the "<b>{response_from}</b>" custom
                    account, shipments will be processed from your registered
                    address by default. For shipments from a different pickup
                    address, please use our <b>'Via Bolt'</b> Account during the
                    booking process to specify the desired pickup location.
                  </>
                )}
            </div>

            <div className="button__shipment_confirmation_wrap">
              {((response_from !== "Via Bolt" &&
                account_type !== "client" &&
                account_type !== "customCarrier") ||
                (userBalance >= total &&
                  paymentType === "pay_via_balance")) && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ borderColor: "var(--color-brand)", borderRadius: "3px" }}
                  type="submit"
                  onClick={CreateShipment}
                  disabled={StepCheck?.Disable === "final"}
                  disableElevation
                >
                  {StepCheck?.Disable === "final" ? (
                    <CircularLoader />
                  ) : (
                    "Pay & book shipment"
                  )}
                </Button>
              )}

              {isViaBoltOrCustom && addOtherCard && (
                <Button
                  positive
                  form="payment-form"
                  content="Submit"
                  onClick={(event) => {
                    // call stripe form's submit (StripeCreditCardForm should handle calling SetStepCheck when card created)
                    if (childRef?.current?.fireSubmit)
                      childRef.current.fireSubmit(event);
                  }}
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ borderColor: "var(--color-brand)", borderRadius: "3px" }}
                  disabled={StepCheck?.Disable === "final"}
                  disableElevation
                >
                  {StepCheck?.Disable === "final" ? (
                    <CircularLoader />
                  ) : (
                    "Pay & book shipment"
                  )}
                </Button>
              )}

              {isViaBoltOrCustom &&
                addOtherCard === false &&
                paymentType === "pay_via_card" && (
                  <Button
                    positive
                    form="payment-form"
                    content="Submit"
                    onClick={(event) => {
                      // set top-level credit_card_payment_method_id (previous behavior)
                      SetStepCheck((prevStepCheck) => ({
                        ...prevStepCheck,
                        credit_card_payment_method_id: paymentMethodId,
                      }));
                      // store the event so the effect can call CreateShipment
                      setGlobalEventHolder(event);
                    }}
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ borderColor: "var(--color-brand)", borderRadius: "3px" }}
                    disabled={StepCheck?.Disable === "final"}
                    disableElevation
                  >
                    {StepCheck?.Disable === "final" ? (
                      <CircularLoader />
                    ) : (
                      "Pay & book shipment"
                    )}
                  </Button>
                )}

              <Button
                variant="contained"
                color="error"
                size="small"
                style={{
                  marginRight: "5px",
                  marginLeft: "10px",
                  borderRadius: "3px",
                }}
                onClick={() => {
                  SetStepCheck((prev) => ({
                    ...prev,
                    Final_Order: false,
                  }));
                }}
                disabled={StepCheck?.Disable === "final"}
                disableElevation
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
